<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useStore } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import ConversationApi from 'dashboard/api/inbox/conversation';
import QaDashboardApi from 'dashboard/api/qaDashboard';

const { t } = useI18n();
const showAlert = useAlert;
const store = useStore();
const { accountScopedRoute } = useAccount();

const isLoading = ref(false);
const isTemplateSaving = ref(false);
const isTemplateLoading = ref(false);
const selectedAgentId = ref(null);
const selectedTemplateId = ref('');
const templateName = ref('');
const intentQuery = ref('');
const templateText = ref('');
const templates = ref([]);
const rows = ref([]);

const normalize = text => String(text || '').trim();

const agents = computed(() => store.getters['agents/getAgents'] || []);
const selectedAgent = computed(() =>
  agents.value.find(agent => Number(agent.id) === Number(selectedAgentId.value))
);
const selectedTemplate = computed(() =>
  templates.value.find(template => template.id === selectedTemplateId.value)
);

const stats = computed(() => {
  const total = rows.value.length;
  const correct = rows.value.filter(row => row.status === 'correct').length;
  const wrong = total - correct;
  return { total, correct, wrong };
});

const expectedTemplate = computed(() => {
  const agentName = selectedAgent.value?.name || '';
  return normalize(templateText.value).replaceAll('{agent_name}', agentName);
});

const ensureAgentsLoaded = async () => {
  if (agents.value.length) return;
  await store.dispatch('agents/get');
};

const loadTemplates = async () => {
  isTemplateLoading.value = true;
  try {
    const response = await QaDashboardApi.getTemplates();
    templates.value = response?.data?.items || [];
  } catch (error) {
    showAlert(
      error.response?.data?.error ||
        t('QA_DASHBOARD.TEMPLATE_AUDIT.ERROR_LOAD_TEMPLATES')
    );
  } finally {
    isTemplateLoading.value = false;
  }
};

const applyTemplate = template => {
  if (!template) return;
  templateName.value = template.name || '';
  intentQuery.value = template.intent_query || '';
  templateText.value = template.template_text || '';
};

const onTemplateSelected = () => {
  applyTemplate(selectedTemplate.value);
};

const clearTemplateSelection = () => {
  selectedTemplateId.value = '';
  templateName.value = '';
};

const saveTemplate = async () => {
  const name = normalize(templateName.value);
  const query = normalize(intentQuery.value);
  const text = templateText.value.toString();
  if (!name || !query || !normalize(text)) return;

  isTemplateSaving.value = true;
  try {
    if (selectedTemplate.value) {
      await QaDashboardApi.updateTemplate(selectedTemplate.value.id, {
        name,
        intent_query: query,
        template_text: text,
      });
    } else {
      const response = await QaDashboardApi.createTemplate({
        name,
        intent_query: query,
        template_text: text,
      });
      selectedTemplateId.value = response?.data?.item?.id || '';
    }
    await loadTemplates();
    if (!selectedTemplateId.value) {
      const matched = templates.value.find(template => template.name === name);
      selectedTemplateId.value = matched?.id || '';
    }
  } catch (error) {
    showAlert(
      error.response?.data?.error ||
        t('QA_DASHBOARD.TEMPLATE_AUDIT.ERROR_SAVE_TEMPLATE')
    );
  } finally {
    isTemplateSaving.value = false;
  }
};

const deleteTemplate = async () => {
  if (!selectedTemplate.value) return;
  try {
    await QaDashboardApi.deleteTemplate(selectedTemplate.value.id);
    clearTemplateSelection();
    await loadTemplates();
  } catch (error) {
    showAlert(
      error.response?.data?.error ||
        t('QA_DASHBOARD.TEMPLATE_AUDIT.ERROR_DELETE_TEMPLATE')
    );
  }
};

const fetchAllSearchResults = async query => {
  const perPage = 25;
  const all = [];
  let page = 1;

  while (true) {
    // conversation search endpoint is paginated; fetch multiple pages to improve audit coverage
    const response = await ConversationApi.search({ q: query, page });
    const payload = response?.data?.payload || [];
    if (!payload.length) break;
    all.push(...payload);
    if (payload.length < perPage) break;
    page += 1;
  }

  // Search can return duplicate conversation rows when multiple messages match.
  const uniqueById = new Map();
  all.forEach(conversation => {
    uniqueById.set(Number(conversation.id), conversation);
  });
  return Array.from(uniqueById.values());
};

const evaluateConversation = conversation => {
  const agentName = selectedAgent.value?.name;
  const expected = expectedTemplate.value;
  const messages = conversation.messages || [];
  const outgoingMessages = messages.filter(
    message =>
      Number(message.message_type) === 1 &&
      normalize(message.sender_name) === normalize(agentName)
  );

  const matched = outgoingMessages.some(
    message => normalize(message.content) === expected
  );

  const status = matched ? 'correct' : 'wrong';
  const reason = matched
    ? t('QA_DASHBOARD.TEMPLATE_AUDIT.RESULT.REASON_MATCHED')
    : outgoingMessages.length
      ? t('QA_DASHBOARD.TEMPLATE_AUDIT.RESULT.REASON_TEMPLATE_MISMATCH')
      : t('QA_DASHBOARD.TEMPLATE_AUDIT.RESULT.REASON_NO_AGENT_REPLY');

  return {
    id: conversation.id,
    status,
    reason,
    customerMessage:
      messages.find(message => Number(message.message_type) === 0)?.content ||
      '-',
    agentMessage: outgoingMessages[0]?.content || '-',
    conversationRoute: accountScopedRoute('inbox_conversation', {
      conversation_id: conversation.id,
    }),
  };
};

const runAudit = async () => {
  if (
    !selectedAgent.value ||
    !normalize(intentQuery.value) ||
    !expectedTemplate.value
  ) {
    return;
  }

  isLoading.value = true;
  rows.value = [];
  try {
    const conversations = await fetchAllSearchResults(
      normalize(intentQuery.value)
    );
    rows.value = conversations.map(evaluateConversation);
  } catch (error) {
    showAlert(
      error.response?.data?.error ||
        t('QA_DASHBOARD.TEMPLATE_AUDIT.ERROR_RUN_AUDIT')
    );
  } finally {
    isLoading.value = false;
  }
};

onMounted(async () => {
  await ensureAgentsLoaded();
  await loadTemplates();
});
</script>

<template>
  <div class="qta-page">
    <div class="qta-shell">
      <div class="qta-header">
        <div class="qta-header-left">
          <button
            class="qta-side-toggle"
            type="button"
            aria-label="Toggle side navigation"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              width="16"
              height="16"
            >
              <path
                stroke="currentColor"
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M9 4v16M4 6a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2z"
              />
            </svg>
          </button>
          <h1 class="qta-title">
            {{ t('QA_DASHBOARD.TEMPLATE_AUDIT.TITLE') }}
          </h1>
        </div>
        <button class="qta-save-btn" type="button" disabled>Save view</button>
      </div>

      <div class="qta-content">
        <section class="qta-card">
          <div class="qta-card-header">
            <div class="qta-card-title-wrap">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                width="20"
                height="20"
              >
                <path
                  stroke="currentColor"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 8h.01M11 12h1v4h1M3 12a9 9 0 1 0 18.001 0A9 9 0 0 0 3 12"
                />
              </svg>
              <h2 class="qta-card-title">
                {{ t('QA_DASHBOARD.TEMPLATE_AUDIT.TITLE') }}
              </h2>
            </div>
          </div>
          <div class="qta-section">
            <p class="qta-desc">
              {{ t('QA_DASHBOARD.TEMPLATE_AUDIT.DESCRIPTION') }}
            </p>
          </div>
        </section>

        <section class="qta-card">
          <div class="qta-card-header">
            <div class="qta-card-title-wrap">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                width="20"
                height="20"
              >
                <path
                  stroke="currentColor"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M4 19a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2H8l-2-2H6a2 2 0 0 0-2 2z"
                />
              </svg>
              <h2 class="qta-card-title">Audit setup</h2>
            </div>

            <button
              class="qta-secondary-btn"
              type="button"
              :disabled="!selectedTemplateId"
              @click="clearTemplateSelection"
            >
              New
            </button>
          </div>

          <div class="qta-section qta-section--no-bottom">
            <div class="qta-form-grid">
              <label class="qta-field qta-field--span-2">
                <span class="qta-label">{{
                  t('QA_DASHBOARD.TEMPLATE_AUDIT.SAVED_TEMPLATE')
                }}</span>
                <select
                  v-model="selectedTemplateId"
                  class="qta-input"
                  :disabled="isTemplateLoading"
                  @change="onTemplateSelected"
                >
                  <option value="">
                    {{ t('QA_DASHBOARD.TEMPLATE_AUDIT.SELECT_SAVED_TEMPLATE') }}
                  </option>
                  <option
                    v-for="template in templates"
                    :key="template.id"
                    :value="template.id"
                  >
                    {{ template.name }}
                  </option>
                </select>
              </label>

              <label class="qta-field">
                <span class="qta-label">{{
                  t('QA_DASHBOARD.TEMPLATE_AUDIT.TEMPLATE_NAME')
                }}</span>
                <input
                  v-model="templateName"
                  class="qta-input"
                  :placeholder="
                    t('QA_DASHBOARD.TEMPLATE_AUDIT.TEMPLATE_NAME_PLACEHOLDER')
                  "
                />
              </label>

              <label class="qta-field">
                <span class="qta-label">{{
                  t('QA_DASHBOARD.FORM.AGENT')
                }}</span>
                <select v-model="selectedAgentId" class="qta-input">
                  <option :value="null">
                    {{ t('QA_DASHBOARD.FORM.SELECT_AGENT') }}
                  </option>
                  <option
                    v-for="agent in agents"
                    :key="agent.id"
                    :value="agent.id"
                  >
                    {{ agent.name }}
                  </option>
                </select>
              </label>

              <label class="qta-field qta-field--span-2">
                <span class="qta-label">{{
                  t('QA_DASHBOARD.TEMPLATE_AUDIT.INTENT_QUERY')
                }}</span>
                <input
                  v-model="intentQuery"
                  class="qta-input"
                  :placeholder="
                    t('QA_DASHBOARD.TEMPLATE_AUDIT.INTENT_QUERY_PLACEHOLDER')
                  "
                />
              </label>
            </div>

            <label class="qta-field qta-field--full">
              <span class="qta-label">{{
                t('QA_DASHBOARD.TEMPLATE_AUDIT.TEMPLATE')
              }}</span>
              <textarea
                v-model="templateText"
                rows="4"
                class="qta-input qta-textarea"
                :placeholder="
                  t('QA_DASHBOARD.TEMPLATE_AUDIT.TEMPLATE_PLACEHOLDER')
                "
              />
            </label>

            <div class="qta-form-footer">
              <p class="qta-hint">
                {{ t('QA_DASHBOARD.TEMPLATE_AUDIT.HINT') }}
              </p>

              <div class="qta-actions">
                <button
                  type="button"
                  class="qta-secondary-btn"
                  :disabled="isTemplateSaving"
                  @click="saveTemplate"
                >
                  {{
                    isTemplateSaving
                      ? t('QA_DASHBOARD.TEMPLATE_AUDIT.SAVING_TEMPLATE')
                      : selectedTemplate
                        ? t('QA_DASHBOARD.TEMPLATE_AUDIT.UPDATE_TEMPLATE')
                        : t('QA_DASHBOARD.TEMPLATE_AUDIT.SAVE_TEMPLATE')
                  }}
                </button>

                <button
                  type="button"
                  class="qta-danger-btn"
                  :disabled="!selectedTemplate || isTemplateSaving"
                  @click="deleteTemplate"
                >
                  {{ t('QA_DASHBOARD.TEMPLATE_AUDIT.DELETE_TEMPLATE') }}
                </button>

                <button
                  type="button"
                  class="qta-primary-btn"
                  :disabled="isLoading"
                  @click="runAudit"
                >
                  {{
                    isLoading
                      ? t('QA_DASHBOARD.FORM.RUNNING')
                      : t('QA_DASHBOARD.FORM.RUN_AUDIT')
                  }}
                </button>
              </div>
            </div>
          </div>
        </section>

        <div class="qta-metrics">
          <div class="qta-metric">
            <div class="qta-metric-label">
              {{ t('QA_DASHBOARD.STATS.TOTAL') }}
            </div>
            <div class="qta-metric-value">{{ stats.total }}</div>
          </div>
          <div class="qta-metric">
            <div class="qta-metric-label">
              {{ t('QA_DASHBOARD.STATS.CORRECT') }}
            </div>
            <div class="qta-metric-value qta-metric-value--good">
              {{ stats.correct }}
            </div>
          </div>
          <div class="qta-metric">
            <div class="qta-metric-label">
              {{ t('QA_DASHBOARD.STATS.WRONG') }}
            </div>
            <div class="qta-metric-value qta-metric-value--bad">
              {{ stats.wrong }}
            </div>
          </div>
        </div>

        <section class="qta-card">
          <div class="qta-card-header">
            <div class="qta-card-title-wrap">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                width="20"
                height="20"
              >
                <path
                  stroke="currentColor"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M8 7h13M8 12h13M8 17h13M3 7h.01M3 12h.01M3 17h.01"
                />
              </svg>
              <h2 class="qta-card-title">Results</h2>
            </div>
          </div>

          <div class="qta-table-wrap">
            <table class="qta-table">
              <thead>
                <tr>
                  <th class="qta-th">
                    {{ t('QA_DASHBOARD.TABLE.CONVERSATION') }}
                  </th>
                  <th class="qta-th">{{ t('QA_DASHBOARD.TABLE.STATUS') }}</th>
                  <th class="qta-th">
                    {{ t('QA_DASHBOARD.TABLE.CUSTOMER_MESSAGE') }}
                  </th>
                  <th class="qta-th">
                    {{ t('QA_DASHBOARD.TABLE.AGENT_MESSAGE') }}
                  </th>
                  <th class="qta-th">{{ t('QA_DASHBOARD.TABLE.REMARK') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="isLoading">
                  <td class="qta-td qta-td--empty" colspan="5">
                    <div class="qta-loading-row">
                      <div class="qta-spinner" />
                      <span>{{ t('QA_DASHBOARD.FORM.RUNNING') }}</span>
                    </div>
                  </td>
                </tr>

                <tr v-for="row in rows" :key="row.id">
                  <td class="qta-td">
                    <RouterLink class="qta-link" :to="row.conversationRoute">
                      #{{ row.id }}
                    </RouterLink>
                  </td>
                  <td class="qta-td">
                    <span
                      class="qta-status"
                      :class="
                        row.status === 'correct'
                          ? 'qta-status--ok'
                          : 'qta-status--bad'
                      "
                    >
                      {{
                        row.status === 'correct'
                          ? t('QA_DASHBOARD.TABLE.CORRECT')
                          : t('QA_DASHBOARD.TABLE.WRONG')
                      }}
                    </span>
                  </td>
                  <td class="qta-td">{{ row.customerMessage }}</td>
                  <td class="qta-td">{{ row.agentMessage }}</td>
                  <td class="qta-td">{{ row.reason }}</td>
                </tr>

                <tr v-if="!rows.length && !isLoading">
                  <td class="qta-td qta-td--empty" colspan="5">
                    {{ t('QA_DASHBOARD.TABLE.EMPTY') }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </div>
  </div>
</template>

<style scoped>
.qta-page {
  width: 100%;
  min-height: 100vh;
  min-height: 100dvh;
  background: #ffffff;
  color: #111827;
}

.qta-shell {
  max-width: 1180px;
  margin: 0 auto;
  padding: 16px 0 40px;
}

.qta-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 0;
  border-bottom: 1px solid #e5e7eb;
  margin-bottom: 16px;
  position: sticky;
  top: 0;
  z-index: 5;
  background: #ffffff;
}

.qta-header-left {
  display: flex;
  align-items: center;
  gap: 10px;
}

.qta-side-toggle {
  width: 36px;
  height: 36px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #ffffff;
  color: #111827;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.qta-side-toggle:hover {
  background: #f9fafb;
}

.qta-title {
  margin: 0;
  font-size: 18px;
  line-height: 1.2;
  font-weight: 700;
  color: #111827;
}

.qta-save-btn {
  height: 30px;
  padding: 0 12px;
  border-radius: 8px;
  border: 1px solid #d9dde3;
  background: #ffffff;
  color: #9ca3af;
  font-size: 13px;
  font-weight: 600;
  cursor: not-allowed;
}

.qta-content {
  display: grid;
  gap: 16px;
}

.qta-card {
  border-radius: 4px;
  border: 1px solid #d8dee5;
  background: #ffffff;
  overflow: hidden;
}

.qta-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 18px 20px;
  border-bottom: 1px solid #e5e7eb;
}

.qta-card-title-wrap {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  color: #111827;
}

.qta-card-title {
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: #111827;
}

.qta-section {
  padding: 16px 20px 20px;
}

.qta-section--no-bottom {
  padding-bottom: 0;
}

.qta-desc {
  margin: 0;
  font-size: 13px;
  line-height: 1.5;
  color: #6b7280;
}

.qta-form-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
}

.qta-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
}

.qta-field--span-2 {
  grid-column: span 2 / span 2;
}

.qta-field--full {
  margin-top: 12px;
}

.qta-label {
  font-size: 12px;
  font-weight: 700;
  color: #111827;
}

.qta-input {
  width: 100%;
  border: 1px solid #d9dde3;
  border-radius: 10px;
  background: #ffffff;
  color: #111827;
  padding: 10px 12px;
  font-size: 13px;
  font-weight: 600;
  outline: none;
}

.qta-input:focus {
  border-color: #93c5fd;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.12);
}

.qta-textarea {
  resize: vertical;
  min-height: 96px;
  font-weight: 600;
}

.qta-form-footer {
  margin-top: 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  padding-bottom: 16px;
}

.qta-hint {
  margin: 0;
  font-size: 12px;
  color: #6b7280;
  line-height: 1.4;
}

.qta-actions {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.qta-primary-btn,
.qta-secondary-btn,
.qta-danger-btn {
  height: 36px;
  border-radius: 10px;
  padding: 0 14px;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  border: 1px solid transparent;
  background: #ffffff;
  color: #111827;
}

.qta-secondary-btn {
  border-color: #d1d5db;
}
.qta-secondary-btn:hover {
  background: #f9fafb;
}

.qta-danger-btn {
  border-color: #fecaca;
  color: #b91c1c;
  background: #ffffff;
}
.qta-danger-btn:hover {
  background: #fef2f2;
}

.qta-primary-btn {
  border-color: #111827;
  background: #111827;
  color: #ffffff;
}
.qta-primary-btn:hover {
  opacity: 0.92;
}

.qta-primary-btn:disabled,
.qta-secondary-btn:disabled,
.qta-danger-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.qta-metrics {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
}
@media (max-width: 1100px) {
  .qta-form-grid {
    grid-template-columns: 1fr;
  }
  .qta-field--span-2 {
    grid-column: auto;
  }
  .qta-metrics {
    grid-template-columns: 1fr;
  }
}

.qta-metric {
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background: #f8fafc;
  padding: 12px;
}
.qta-metric-label {
  font-size: 12px;
  font-weight: 700;
  color: #6b7280;
}
.qta-metric-value {
  margin-top: 8px;
  font-size: 24px;
  line-height: 28px;
  font-weight: 800;
  color: #111827;
}
.qta-metric-value--good {
  color: #16a34a;
}
.qta-metric-value--bad {
  color: #dc2626;
}

.qta-table-wrap {
  overflow-x: auto;
}

.qta-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
}

.qta-th,
.qta-td {
  border-bottom: 1px solid #edf1f5;
  padding: 10px 12px;
  text-align: left;
  vertical-align: top;
}

.qta-th {
  font-weight: 800;
  color: #111827;
  background: #f8fafc;
  position: sticky;
  top: 0;
}

.qta-td--empty {
  padding: 24px 12px;
  text-align: center;
  color: #6b7280;
}

.qta-link {
  font-weight: 800;
  color: #111827;
  text-decoration: underline;
  text-underline-position: from-font;
}

.qta-status {
  font-weight: 800;
}
.qta-status--ok {
  color: #16a34a;
}
.qta-status--bad {
  color: #dc2626;
}

.qta-loading-row {
  display: inline-flex;
  align-items: center;
  gap: 10px;
}

.qta-spinner {
  width: 18px;
  height: 18px;
  border: 3px solid #e5e7eb;
  border-top-color: #6b7280;
  border-radius: 50%;
  animation: qta-spin 0.8s linear infinite;
}

@keyframes qta-spin {
  to {
    transform: rotate(360deg);
  }
}

/* Template Audit is always "white report" (hardcoded colors, no dark mode switching needed) */
::global(body.dark) .qta-page,
::global(body.dark) .qta-shell,
::global(body.dark) .qta-card,
::global(body.dark) .qta-header,
::global(body.dark) .qta-metric,
::global(body.dark) .qta-input,
::global(body.dark) .qta-textarea,
::global(body.dark) .qta-th,
::global(body.dark) .qta-td {
  background: #ffffff !important;
  color: #111827 !important;
}
</style>
